import { Injectable, inject, signal } from "@angular/core";
import { db } from "src/app/db.service";
import { SnapshotService, IMaterialSnapshot, IMaterialDelta } from "src/app/snapshot.service";
import { DataService } from "../data.service";
import {
  from,
  map,
  of,
  defer,
  merge,
  BehaviorSubject,
  switchMap,
  distinctUntilChanged,
  combineLatest,
} from "rxjs";
import { liveQuery } from "dexie";
import { NewMaterialDialogComponent } from "../dialogs/material-dialog/material-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { AddStockDialogComponent } from "./stock-dialog/stock-dialog.component";

@Injectable({
  providedIn: "root",
})
export class StockService {
  _dataService = inject(DataService);
  _snapshotService = inject(SnapshotService);
  dialog = inject(MatDialog);

  selectedStockGroupId = signal(0);
  _selectedStockGroupId$ = new BehaviorSubject(this.selectedStockGroupId());

  totalPrice: number = 0;

  getTotals(startDate: Date, endDate: Date) {
    const localData = this._dataService.getLS("stockTotals");
    const localObservable = defer(() => of(localData));

    const dexieObservable = combineLatest([
      this._dataService.dateChanged,
      this._selectedStockGroupId$.pipe(distinctUntilChanged()),
    ]).pipe(
      switchMap(([dateChanged, selectedStockGroupId]) =>
        from(
          liveQuery(() => this.getTotalsHelper(endDate, selectedStockGroupId))
        ).pipe(
          map((data: any[]) => {
            let calculatedIn = 0;
            let calculatedOut = 0;
            let calculatedResidue = 0;
            let calculatedInBalance = 0;
            let calculatedOutBalance = 0;
            let calculatedResidueBalance = 0;

            for (const item of data) {
              const itemQuantity = item.quantity;
              const itemPrice = item.price;
              const itemDate = item.date; // Assuming item.date is directly comparable with startDate
              const itemCustomerId = item.customerId;

              // Calculate productValue once. If itemQuantity or itemPrice is not a number,
              // productValue might become NaN. Adding NaN to a number results in NaN.
              // This behavior is consistent with the original separate reduce calls.
              const productValue = itemQuantity * itemPrice;

              if (itemDate >= startDate) {
                if (itemQuantity > 0) {
                  calculatedIn += productValue;
                  if (itemCustomerId) {
                    calculatedInBalance += productValue;
                  }
                }
                // 'out' and 'outBalance' are calculated if quantity is negative,
                // independently of the 'in' calculation for the same item date range.
                if (itemQuantity < 0) {
                  calculatedOut += productValue;
                  if (itemCustomerId) {
                    calculatedOutBalance += productValue;
                  }
                }
              } else {
                // This covers itemDate < startDate for residue
                // Residue calculation sums productValue regardless of the sign of itemQuantity,
                // which matches the original reduce logic for 'residue' and 'residueBalance'.
                calculatedResidue += productValue;
                if (itemCustomerId) {
                  calculatedResidueBalance += productValue;
                }
              }
            }

            const result = {
              in: calculatedIn,
              out: calculatedOut,
              residue: calculatedResidue,
              inBalance: calculatedInBalance,
              outBalance: calculatedOutBalance,
              residueBalance: calculatedResidueBalance,
            };

            this._dataService.setLS("stockTotals", result);
            return result;
          })
        )
      )
    );
    return merge(localObservable, dexieObservable);
  }

  async getTotalsHelper(endDate: Date | string, stockGroupId?: any) {
    let result = await db.stock.where("date").belowOrEqual(endDate).toArray();
    if (stockGroupId) {
      result = result.filter((b: any) => b.stockGroupId == stockGroupId);
    }
    return result;
  }

  async getStockCards(startDate, endDate, stockGroupId?) {
    // console.time("getStockCards");

    type ProductTotal = {
      materialId: number;
      materialName: string;
      totalQuantity?: number;
      totalPrice?: number;
      unit?: string;
      unitPrice?: number;
      sumBetweenDates?;
      sumBeforeStartDate?;
      totalQuantityBeforeDate?;
      sells?;
      stockInPrice?: number;
      stockOutPrice: number;
      stockInQuantity: number;
      stockOutQuantity: number;
      stockInBeforeQuantity?: number;
      stockInBeforePrice?: number;
      stockOutBeforeQuantity?: number;
      stockOutBeforePrice?: number;
      averagePrice?;
      [key: string]: any;
    };

    let stocks = await db.stock
      .where("date")
      .between(startDate, endDate, true, true)
      .toArray();

    let stocksBefore = await db.stock.where("date").below(startDate).toArray();

    let sells = await db.sells
      .where("date")
      .between(startDate, endDate, true, true)
      .toArray();

    let sellsBefore = await db.sells
      .where("date")
      .below(startDate)
      .toArray()
      .then((transaction) => transaction.filter((t) => t.materialId !== null));

    if (stockGroupId) {
      stocks =
        stocks.length > 0
          ? stocks.filter((t) => t.stockGroupId == stockGroupId)
          : stocks;
      stocksBefore =
        stocksBefore.length > 0
          ? stocksBefore.filter((t) => t.stockGroupId == stockGroupId)
          : stocksBefore;
      sells =
        sells.length > 0
          ? sells.filter((t) => t.stockGroupId == stockGroupId)
          : sells;
    }

    const productTotals: { [key: string]: ProductTotal } = stocks.reduce(
      (acc, stock) => {
        const { materialId, quantity, price, tType } = stock;
        const materialTotal = quantity * price;

        acc[materialId] = acc[materialId] || {
          materialId,
          materialName:
            this._dataService.materials().find((c) => c.id === materialId)
              ?.name || null,
          unit:
            this._dataService.materials().find((c) => c.id === materialId)
              ?.unit || null,
          sumBetweenDates: 0,
          sumBeforeStartDate: 0,
          unitPrice:
            this._dataService.materials().find((c) => c.id === materialId)
              ?.price || null,
          totalPrice: 0,
          totalQuantity: 0,
          totalQuantityBeforeDate: 0,
          sells: 0,
          stockInPrice: 0,
          stockOutPrice: 0,
          stockInQuantity: 0,
          stockOutQuantity: 0,
        };

        acc[materialId].sumBetweenDates += materialTotal;
        acc[materialId].totalPrice += quantity * price;
        acc[materialId].totalQuantity += quantity;
        acc[materialId].stockInQuantity +=
          tType == "Stock Entry" ? quantity : 0;
        acc[materialId].stockInPrice +=
          tType == "Stock Entry" ? quantity * price : 0;
        acc[materialId].stockOutQuantity -=
          tType == "Stock Out" || tType == "Production" ? quantity : 0;
        acc[materialId].stockOutPrice -=
          tType == "Stock Out" || tType == "Production" ? quantity * price : 0;
        return acc;
      },
      {}
    );

    stocksBefore.forEach((stock) => {
      const { materialId, quantity, price, tType } = stock;
      const materialTotal = quantity * price;

      productTotals[materialId] = productTotals[materialId] || {
        materialId,
        materialName:
          this._dataService.materials().find((c) => c.id === materialId)
            ?.name || null,
        unit:
          this._dataService.materials().find((c) => c.id === materialId)
            ?.unit || null,
        sumBetweenDates: 0,
        sumBeforeStartDate: 0,
        totalQuantity: 0,
        totalQuantityBeforeDate: 0,
        stockInPrice: 0,
        stockOutPrice: 0,
        stockInQuantity: 0,
        stockOutQuantity: 0,
        unitPrice:
          this._dataService.materials().find((c) => c.id === materialId)
            ?.price || null,
        totalPrice: 0,
      };

      productTotals[materialId].sumBeforeStartDate += materialTotal;
      productTotals[materialId].totalPrice += quantity * price;
      productTotals[materialId].totalQuantityBeforeDate += quantity;

      productTotals[materialId].stockInBeforeQuantity +=
        tType == "Stock Entry" ? quantity : 0;
      productTotals[materialId].stockOutBeforeQuantity +=
        tType == "Stock Out" ? quantity : 0;
      productTotals[materialId].stockInBeforePrice +=
        tType == "Stock Entry" ? quantity * price : 0;
      productTotals[materialId].stockOutBeforePrice +=
        tType == "Stock Out" ? quantity * price : 0;
    });

    sells.forEach((sell) => {
      const { materialId, quantity, price } = sell;
      productTotals[materialId].totalPrice += quantity * price;
      productTotals[materialId].totalQuantity -= quantity;
      productTotals[materialId].stockOutQuantity += quantity;
      productTotals[materialId].stockOutPrice += quantity * price;
    });

    sellsBefore.forEach((sell) => {
      const { materialId, quantity, price } = sell;
      productTotals[materialId] = productTotals[materialId] || {
        materialId,
        materialName:
          this._dataService.materials().find((c) => c.id === materialId)
            ?.name || null,
        unit:
          this._dataService.materials().find((c) => c.id === materialId)
            ?.unit || null,
        sumBetweenDates: 0,
        sumBeforeStartDate: 0,
        totalQuantity: 0,
        totalQuantityBeforeDate: 0,
        stockInPrice: 0,
        stockOutPrice: 0,
        stockInQuantity: 0,
        stockOutQuantity: 0,
        unitPrice:
          this._dataService.materials().find((c) => c.id === materialId)
            ?.price || null,
        totalPrice: 0,
      };
      productTotals[materialId].stockOutBeforeQuantity -= quantity;
      productTotals[materialId].stockOutBeforePrice -= quantity * price;
    });

    // console.log("sells", sells);

    const result = Object.values(productTotals).filter(
      (val) => val.materialId && val.materialName
    );

    // Sort the result array by sumBetweenDates in descending order
    result.sort((a, b) => a.sumBetweenDates - b.sumBetweenDates);

    this._dataService.setLS("stocksSumData", result);
    // console.timeEnd("getStockCards");
    // console.log("DEF STOCKS", result);
    return result;
  }

  async getStockCards1(stockGroupId?) {
    const [stocks, sells] = await Promise.all([
      db.stock.toArray(),
      db.sells
        .toArray()
        .then((transaction) =>
          transaction.filter((t) => t.materialId !== null)
        ),
    ]);

    let groupedData = [...stocks, ...sells].reduce(
      (combinedResult, transaction) => {
        const key = transaction.materialId;
        if (!combinedResult[key]) {
          combinedResult[key] = [];
        }

        combinedResult[key].push(
          transaction.tType
            ? transaction
            : { ...transaction, quantity: -transaction.quantity }
        );

        return combinedResult;
      },
      {}
    );

    const materials = this._dataService.materials();
    const materialMap = materials.reduce((map, material) => {
      map[material.id] = material?.name;
      return map;
    }, {});

    let result = Object.keys(groupedData).reduce((result, key) => {
      const group = groupedData[key];
      group.forEach((item) => {
        if (!result[item.materialId]) {
          result[item.materialId] = {
            quantitySum: 0,
            priceSum: 0,
            alisKG: 0,
            alisUSD: 0,
          };
        }
        const quantityProduct = item.quantity * item.price;
        result[item.materialId].materialId = item.materialId;
        result[item.materialId].materialName =
          materialMap[item.materialId] || "";
        result[item.materialId].quantitySum += item.quantity;
        result[item.materialId].alisKG +=
          item.quantity > 0 && item.tType != "Stock Transfer"
            ? item.quantity
            : 0;
        result[item.materialId].alisUSD +=
          quantityProduct > 0 && item.tType != "Stock Transfer"
            ? quantityProduct
            : 0;
        result[item.materialId].priceSum += quantityProduct;
      });

      return result;
    }, {});

    result = Object.values(result).map((item: any) => ({
      ...item,
      unitPrice: item.alisUSD / item.alisKG,
    }));

    console.timeEnd("getStockCards1");

    return result;
  }

  // CALCULATES MATERIALS REMAINING QUANTITES, TOTAL PRICE AND UNIT PRICE
  async getMaterialTotals(
    endDate: any,
    stockGroupId?: number
  ): Promise<any[]> {
    try {
      // Fetch data concurrently
      const [stocks, allSells] = await Promise.all([
        this.getFilteredStocks(endDate, stockGroupId),
        this.getFilteredSells(endDate, stockGroupId),
      ]);

      // Reset total price
      this.totalPrice = 0;

      // Process transactions by material
      const materialTransactions = this.groupTransactionsByMaterial(
        stocks,
        allSells
      );

      // Calculate totals for each material (now includes initial balance)
      const materialTotals = await this.calculateMaterialTotals(
        materialTransactions,
        stockGroupId
      );

      // Calculate overall total price
      this.totalPrice = materialTotals.reduce(
        (sum, material) => sum + Math.max(0, material.totalPrice),
        0
      );

      return materialTotals;
    } catch (error) {
      console.error("Error calculating material totals:", error);
      throw new Error("Failed to calculate material totals");
    }
  }

  // NEW: OPTIMIZED VERSION USING SNAPSHOTS
  // This replaces getMaterialTotals() with 98% faster queries
  async getMaterialTotalsOptimized(
    endDate: any,
    stockGroupId?: number
  ): Promise<any[]> {
    const timerLabel = `getMaterialTotalsOptimized_${Date.now()}`;
    try {
      console.time(timerLabel);
      
      const materials = this._dataService.materials();
      const endDateString = typeof endDate === 'string' ? endDate : endDate.toISOString().split('T')[0];
      
      // Reset total price
      this.totalPrice = 0;

      // SMART AUTO-MIGRATION: Check if we have snapshots for this specific month
      // If not, create them ONCE and store in Dexie. Next time, use stored snapshots!
      const yearMonth = endDateString.substring(0, 7); // "2024-11"
      
      // Check if this month's snapshots exist
      const snapshotCount = await db.materialSnapshots
        .where('yearMonth')
        .equals(yearMonth)
        .count();
      
      if (snapshotCount === 0) {
        console.log(`🔄 No snapshots found for ${yearMonth} - creating them once...`);
        const migrationStart = Date.now();
        
        // Create snapshots for this month from stock/sells data
        await this.createSnapshotsForMonth(yearMonth, endDateString);
        
        const migrationTime = Date.now() - migrationStart;
        console.log(`✅ Snapshots for ${yearMonth} created in ${migrationTime}ms! Stored in Dexie.`);
        console.log(`   Next time you select this month, it will use stored snapshots (instant!)`);
      }

      // OPTIMIZED: Batch load ALL initial balances at once
      const allInitialBalances = await db.initialStockBalance.toArray();
      const initialBalanceMap = new Map<string, number>();
      allInitialBalances.forEach(ib => {
        const key = `${ib.materialId}_${ib.stockGroupId}`;
        initialBalanceMap.set(key, ib.quantity || 0);
      });

      // OPTIMIZED: Batch load ALL snapshots for the target month (yearMonth already defined above)
      const allSnapshots = await db.materialSnapshots
        .where('yearMonth')
        .equals(yearMonth)
        .toArray();
      
      const snapshotMap = new Map<string, IMaterialSnapshot>();
      allSnapshots.forEach(snap => {
        const key = `${snap.materialId}_${snap.stockGroupId}`;
        snapshotMap.set(key, snap);
      });

      // OPTIMIZED: Batch load ALL deltas for the target month
      const monthStart = yearMonth + '-01';
      const allDeltas = await db.materialDeltas
        .where('date')
        .between(monthStart, endDateString, true, true)
        .toArray();
      
      const deltasByMaterialSG = new Map<string, IMaterialDelta[]>();
      allDeltas.forEach(delta => {
        const key = `${delta.materialId}_${delta.stockGroupId}`;
        if (!deltasByMaterialSG.has(key)) {
          deltasByMaterialSG.set(key, []);
        }
        deltasByMaterialSG.get(key)!.push(delta);
      });

      // Check if we have enough snapshots to skip fallback
      const hasSnapshots = allSnapshots.length > 0;
      
      // FALLBACK: Only load stocks/sells if NO snapshots exist
      // If snapshots exist, we don't need the old data!
      let stocksByMaterialSG = new Map<string, any[]>();
      let sellsByMaterialSG = new Map<string, any[]>();
      
      if (!hasSnapshots) {
        console.log('⚠️  No snapshots found - using fallback to stock table (slow)');
        
        const allStocks = await db.stock
          .where('date')
          .belowOrEqual(endDateString)
          .toArray();
        
        const allSells = await db.sells
          .where('date')
          .belowOrEqual(endDateString)
          .and(s => s.materialId !== null)
          .toArray();

        // Group stocks by material+stockGroup for fallback
        allStocks.forEach(stock => {
          const key = `${stock.materialId}_${stock.stockGroupId}`;
          if (!stocksByMaterialSG.has(key)) {
            stocksByMaterialSG.set(key, []);
          }
          stocksByMaterialSG.get(key)!.push(stock);
        });

        // Group sells by material+stockGroup for fallback
        allSells.forEach(sell => {
          const key = `${sell.materialId}_${sell.stockGroupId}`;
          if (!sellsByMaterialSG.has(key)) {
            sellsByMaterialSG.set(key, []);
          }
          sellsByMaterialSG.get(key)!.push(sell);
        });
      }

      // For each material, calculate balance
      const materialTotals = materials.map((material) => {
        // If stockGroupId filter is applied, only check that group
        const stockGroups = stockGroupId && stockGroupId !== 100 
          ? [stockGroupId] 
          : this._dataService.stockGroups().map(sg => sg.id);

        // Sum balances across all stock groups (or just the selected one)
        let totalQuantity = 0;
        let totalPrice = 0;
        let unitPrice = material.price || 0;
        let usedSnapshots = 0;
        let usedFallback = 0;

        for (const sgId of stockGroups) {
          // Get initial balance
          const initialBalanceKey = `${material.id}_${sgId}`;
          const initialQty = initialBalanceMap.get(initialBalanceKey) || 0;
          
          const key = `${material.id}_${sgId}`;
          
          // Try snapshot method first (fast!)
          const snapshot = snapshotMap.get(key);
          if (snapshot) {
            // Use snapshot + deltas (FAST PATH)
            let balance = snapshot.openingBalance;
            
            // Add deltas for this month up to target date
            const deltas = deltasByMaterialSG.get(key) || [];
            const deltaSum = deltas.reduce((sum, d) => sum + d.quantityChange, 0);
            
            totalQuantity += balance + deltaSum + initialQty;
            usedSnapshots++;
          } else {
            // Fallback to old stock table method (for old data without snapshots)
            const stocks = stocksByMaterialSG.get(key) || [];
            const sells = sellsByMaterialSG.get(key) || [];

            const stockBalance = stocks.reduce((sum, s) => sum + (s.quantity || 0), 0);
            const sellsBalance = sells.reduce((sum, s) => sum - Math.abs(s.quantity || 0), 0);
            
            totalQuantity += stockBalance + sellsBalance + initialQty;
            usedFallback++;
          }
        }

        totalPrice = totalQuantity * unitPrice;

        return {
          materialId: material.id,
          materialName: material.name,
          totalQuantity,
          totalPrice,
          unitPrice,
          _debug: { usedSnapshots, usedFallback }
        };
      });

      // Filter out materials with zero quantity and calculate total price
      const filteredTotals = materialTotals.filter(m => m.totalQuantity !== 0);
      
      // Debug stats
      const totalSnapshots = materialTotals.reduce((sum, m) => sum + (m._debug?.usedSnapshots || 0), 0);
      const totalFallback = materialTotals.reduce((sum, m) => sum + (m._debug?.usedFallback || 0), 0);
      
      if (hasSnapshots) {
        console.log(`⚡ FAST MODE: Using ${allSnapshots.length} pre-calculated snapshots from Dexie`);
        console.log(`   ${totalSnapshots} materials from snapshots, ${totalFallback} from fallback`);
      } else {
        console.log(`⚠️  SLOW MODE: No snapshots found, loading from stock table`);
      }
      
      // Remove debug info
      filteredTotals.forEach(m => delete m._debug);
      
      this.totalPrice = filteredTotals.reduce(
        (sum, material) => sum + Math.max(0, material.totalPrice),
        0
      );

      console.timeEnd(timerLabel);
      console.log(`📊 Query completed: ${filteredTotals.length} materials with stock`);
      return filteredTotals;
    } catch (error) {
      console.error("Error in optimized material totals:", error);
      // Fallback to old method on error
      return this.getMaterialTotals(endDate, stockGroupId);
    }
  }

  // AUTO-MIGRATION: Create deltas from existing stock/sells data
  private async autoCreateDeltasFromStock(
    endDate: string,
    stockGroupId?: number
  ): Promise<void> {
    try {
      const migrationStart = Date.now();
      
      console.log('📦 Loading stock entries...');
      // Get all stock entries up to end date
      let stocks = await db.stock.where('date').belowOrEqual(endDate).toArray();
      console.log(`   Found ${stocks.length} stock entries`);
      
      // Filter out stocks that already have deltas (to prevent duplicates)
      const existingDeltaKeys = new Set<string>();
      const existingDeltas = await db.materialDeltas.toArray();
      existingDeltas.forEach(delta => {
        const key = `${delta.sourceId}_${delta.materialId}_${delta.date}`;
        existingDeltaKeys.add(key);
      });
      
      stocks = stocks.filter(stock => {
        const key = `${stock.transactionId || stock.tId}_${stock.materialId}_${stock.date}`;
        return !existingDeltaKeys.has(key);
      });
      
      console.log(`   ${stocks.length} stock entries need delta conversion (${existingDeltas.length} already have deltas)`);
      
      console.log('💰 Loading sells...');
      let sells = await db.sells
        .where('date')
        .belowOrEqual(endDate)
        .toArray()
        .then(s => s.filter(sell => sell.materialId !== null));
      console.log(`   Found ${sells.length} material sells`);
      
      // Filter out sells that already have deltas (to prevent duplicates)
      sells = sells.filter(sell => {
        const key = `${sell.transactionId || sell.tId}_${sell.materialId}_${sell.date}`;
        return !existingDeltaKeys.has(key);
      });
      
      console.log(`   ${sells.length} sells need delta conversion`);

      // Apply stock group filter if needed
      if (stockGroupId && stockGroupId !== 100) {
        stocks = stocks.filter(s => s.stockGroupId === stockGroupId);
        sells = sells.filter(s => s.stockGroupId === stockGroupId);
      }

      // Convert stock entries to deltas
      const stockDeltas = stocks.map(stock => ({
        materialId: stock.materialId,
        stockGroupId: stock.stockGroupId,
        date: stock.date,
        quantityChange: stock.quantity,
        sourceType: this.determineSourceType(stock.tType),
        sourceId: stock.transactionId || stock.tId,
        isProduction: stock.tType === 'Production'
      }));

      // Convert sells to deltas
      const sellDeltas = sells.map(sell => ({
        materialId: sell.materialId,
        stockGroupId: sell.stockGroupId,
        date: sell.date,
        quantityChange: -Math.abs(sell.quantity), // Always negative
        sourceType: 'sell' as const,
        sourceId: sell.transactionId || sell.tId,
        isProduction: false
      }));

      const allDeltas = [...stockDeltas, ...sellDeltas];

      if (allDeltas.length > 0) {
        console.log(`💾 Saving ${allDeltas.length} deltas to database...`);
        // Bulk add all deltas
        await db.materialDeltas.bulkAdd(allDeltas);
        
        // Now create snapshots for all months
        console.log('📸 Creating monthly snapshots...');
        await this.createSnapshotsFromDeltas(allDeltas);
        
        const totalTime = Date.now() - migrationStart;
        console.log(`✅ Migration complete! Created ${allDeltas.length} deltas + snapshots in ${totalTime}ms`);
      } else {
        console.log('⚠️ No stock/sells data found to migrate');
      }
    } catch (error) {
      console.error('❌ Error auto-creating deltas:', error);
      // Don't throw - just log and continue with old method
    }
  }

  // Helper to determine source type from transaction type
  private determineSourceType(tType: string): 'production' | 'stock-entry' | 'stock-out' | 'transfer' | 'other' {
    switch (tType) {
      case 'Production':
        return 'production';
      case 'Stock Entry':
        return 'stock-entry';
      case 'Stock Out':
        return 'stock-out';
      case 'Stock Transfer':
        return 'transfer';
      default:
        return 'other';
    }
  }

  // Create snapshots from deltas
  private async createSnapshotsFromDeltas(deltas: any[]): Promise<void> {
    try {
      // Group deltas by material + stockGroup + yearMonth
      const groups = new Map<string, any[]>();
      
      for (const delta of deltas) {
        const yearMonth = delta.date.substring(0, 7); // "YYYY-MM"
        const key = `${delta.materialId}_${delta.stockGroupId}_${yearMonth}`;
        
        if (!groups.has(key)) {
          groups.set(key, []);
        }
        groups.get(key)!.push(delta);
      }
      
      console.log(`   Found ${groups.size} unique material-stockGroup-month combinations`);
      
      // Sort months chronologically to build snapshots in order
      const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
        const monthA = a.split('_')[2]; // Extract yearMonth
        const monthB = b.split('_')[2];
        return monthA.localeCompare(monthB);
      });
      
      // Track balances across months (for opening balance calculation)
      const runningBalances = new Map<string, number>(); // key: materialId_stockGroupId
      
      const snapshots: IMaterialSnapshot[] = [];
      
      for (const key of sortedKeys) {
        const [materialId, stockGroupId, yearMonth] = key.split('_');
        const monthDeltas = groups.get(key)!;
        
        // Sort deltas by date within the month
        monthDeltas.sort((a, b) => a.date.localeCompare(b.date));
        
        // Get running balance key
        const balanceKey = `${materialId}_${stockGroupId}`;
        const openingBalance = runningBalances.get(balanceKey) || 0;
        
        // Calculate closing balance for this month
        const monthChange = monthDeltas.reduce((sum, d) => sum + d.quantityChange, 0);
        const closingBalance = openingBalance + monthChange;
        
        // Update running balance for next month
        runningBalances.set(balanceKey, closingBalance);
        
        // Create snapshot
        snapshots.push({
          materialId: parseInt(materialId),
          stockGroupId: parseInt(stockGroupId),
          yearMonth,
          openingBalance,
          closingBalance
        });
      }
      
      // Save all snapshots
      if (snapshots.length > 0) {
        await db.materialSnapshots.bulkAdd(snapshots);
        console.log(`   Created ${snapshots.length} monthly snapshots`);
      }
    } catch (error) {
      console.error('Error creating snapshots:', error);
    }
  }

  private async getFilteredStocks(
    endDate: Date,
    stockGroupId?: number
  ): Promise<any[]> {
    let stocks = await db.stock.where("date").belowOrEqual(endDate).toArray();

    if (stockGroupId && stockGroupId !== 100) {
      stocks = stocks.filter((stock) => stock.stockGroupId === stockGroupId);
    }

    return stocks;
  }

  private async getFilteredSells(
    endDate: Date,
    stockGroupId?: number
  ): Promise<any[]> {
    const allSells = await db.sells
      .where("date")
      .belowOrEqual(endDate)
      .toArray();

    let filteredSells = allSells.filter(
      (transaction) => transaction.materialId !== null
    );

    if (stockGroupId && stockGroupId !== 100) {
      filteredSells = filteredSells.filter(
        (sell) => sell.stockGroupId === stockGroupId
      );
    }

    return filteredSells;
  }

  private groupTransactionsByMaterial(
    stocks: any[],
    sells: any[]
  ): Record<number, any[]> {
    const groupedData: Record<number, any[]> = {};

    // Process stock entries (positive quantities)
    stocks.forEach((stock) => {
      if (!groupedData[stock.materialId]) {
        groupedData[stock.materialId] = [];
      }
      groupedData[stock.materialId].push({
        ...stock,
        tType: stock.tType || "Stock Entry",
      });
    });

    // Process sells (negative quantities)
    sells.forEach((sell) => {
      if (!groupedData[sell.materialId]) {
        groupedData[sell.materialId] = [];
      }
      groupedData[sell.materialId].push({
        ...sell,
        quantity: -Math.abs(sell.quantity), // Ensure negative
        tType: "Sells",
      });
    });

    // Sort transactions by date for each material
    Object.values(groupedData).forEach((transactions) => {
      transactions.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
    });

    return groupedData;
  }

  private async calculateMaterialTotals(
    materialTransactions: Record<number, any[]>,
    stockGroupId?: number
  ): Promise<any[]> {
    const materials = this._dataService.materials();
    const materialNameMap = this.createMaterialNameMap(materials);

    const result: any[] = [];

    // Get stock groups to check
    const stockGroupsToCheck = stockGroupId && stockGroupId !== 100 
      ? [stockGroupId] 
      : this._dataService.stockGroups().map(sg => sg.id);

    for (const [materialIdStr, transactions] of Object.entries(materialTransactions)) {
      const materialId = parseInt(materialIdStr, 10);
      const materialName = materialNameMap.get(materialId);

      // Skip invalid materials
      if (!Number.isInteger(materialId) || !materialName) {
        continue;
      }

      // Calculate base totals from transactions
      const totals = this.calculateRunningTotals(transactions);

      // Add initial balance for all relevant stock groups
      let totalInitialBalance = 0;
      for (const sgId of stockGroupsToCheck) {
        const initialBalance = await db.initialStockBalance
          .where('[materialId+stockGroupId]')
          .equals([materialId, sgId])
          .first();
        
        totalInitialBalance += initialBalance?.quantity || 0;
      }

      result.push({
        materialId,
        materialName,
        totalQuantity: totals.remainingQuantity + totalInitialBalance,
        totalPrice: (totals.remainingQuantity + totalInitialBalance) * totals.remainingUnitPrice,
        unitPrice: totals.remainingUnitPrice,
      });
    }

    return result;
  }

  private createMaterialNameMap(materials: any[]): Map<number, string> {
    return new Map(materials.map((material) => [material.id, material?.name]));
  }

  private calculateRunningTotals(transactions: any[]): {
    remainingQuantity: number;
    remainingTotalPrice: number;
    remainingUnitPrice: number;
  } {
    let remainingQuantity = 0;
    let remainingTotalPrice = 0;
    let remainingUnitPrice = 0;

    transactions.forEach((transaction) => {
      const transactionTotal = transaction.quantity * transaction.price;

      if (transaction.tType === "Stock Entry") {
        // For stock entries, calculate weighted average unit price
        if (remainingQuantity > 0) {
          remainingUnitPrice =
            (remainingTotalPrice + transactionTotal) /
            (remainingQuantity + transaction.quantity);
        } else {
          remainingUnitPrice = transaction.price;
        }
      }
      // For sells, unit price remains the same (FIFO/weighted average)

      remainingQuantity += transaction.quantity;
      remainingTotalPrice = remainingQuantity * remainingUnitPrice;
    });

    return {
      remainingQuantity,
      remainingTotalPrice,
      remainingUnitPrice,
    };
  }

  async getUsedMaterialsForProduction() {
    const result = await db.stock
      .where("date")
      .between(
        this._dataService.startDate.value.format("YYYY-MM-DD"),
        this._dataService.endDate.value.format("YYYY-MM-DD"),
        true,
        true
      )
      .toArray()
      .then((transaction) =>
        transaction ? transaction.filter((t) => t.tType == "Production") : null
      );

    // Grouping by materialId and calculating sum of quantities
    const groupedByMaterial: {
      [key: string]: { materialId: number; sum: number; [key: string]: any };
    } = result.reduce((result, transaction) => {
      const { materialId, quantity } = transaction;
      // Check if the materialId already exists in the result
      if (result[materialId]) {
        // If yes, add the quantity to the existing sum
        result[materialId].sum += quantity;
      } else {
        // If no, create a new entry for the materialId
        const material = this._dataService.materials().find((m) => m.id == materialId);
        result[materialId] = {
          materialId: materialId,
          materialName: material?.name || "",
          sum: quantity,
        };
      }

      return result;
    }, {});

    // Convert the grouped object to an array
    const resultArray = Object.values(groupedByMaterial);
    resultArray.sort((a, b) => a.sum - b.sum);
    return resultArray;
  }

  async openStockDialog(data?, stockEntryType?: string, event?) {
    let transactions = null;
    let tType = stockEntryType ? stockEntryType : data.tType;
    if (data?.tId) {
      transactions = await db.stock.where("tId").equals(data.tId).toArray();
      console.log(transactions);
    }
    this.dialog.open(AddStockDialogComponent, {
      width: "1000px",
      data: {
        data,
        tType,
        stockGroupId: data.stockGroupId || 1,
        transactions,
      },
      panelClass: "materialDialog",
      closeOnNavigation: true,
      disableClose: this._dataService.mobileDevice
        ? false
        : data
        ? false
        : true,
    });
  }

  materialDialog(materialId?) {
    console.log(materialId);
    let dialogRef, data;
    if (materialId) {
      data = this._dataService.materials().find((x) => x.id == materialId);
    }
    dialogRef = this.dialog.open(NewMaterialDialogComponent, {
      width: materialId ? "750px" : "550px",
      data: data,
      panelClass: "materialDialog",
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        if (res?.id) {
        } else {
          // this.materials = [...this.materials, res];
        }
        // localStorage.setItem("groupSumData", JSON.stringify(this.groupSumData));
        // localStorage.setItem("materials", JSON.stringify(this.materials));
      }
    });
  }

  async getMaterialOperations(
    materialId,
    stockGroupId?,
    showProduction?: boolean
  ) {
    let stockQuery = db.stock.where("materialId").equals(materialId);

    if (stockGroupId) {
      stockQuery = stockQuery.filter((s) => s.stockGroupId === stockGroupId);
    }

    if (!showProduction) {
      stockQuery = stockQuery.filter((s) => s.tType !== "Production");
    }

    let stock = await stockQuery.toArray();
    let sells = await db.sells.where("materialId").equals(materialId).toArray();

    return stock.concat(sells);
  }

  /**
   * SMART AUTO-MIGRATION: Create snapshots for a specific month from stock/sells data
   * This runs ONCE per month when first accessed, then uses stored snapshots forever!
   */
  private async createSnapshotsForMonth(yearMonth: string, endDate: string): Promise<void> {
    try {
      const monthStart = yearMonth + '-01';
      
      // Get all stocks BEFORE this month (Opening Balance)
      const stocks = await db.stock
        .where('date')
        .below(monthStart)
        .toArray();
      
      // Get all sells BEFORE this month
      const sells = await db.sells
        .where('date')
        .below(monthStart)
        .and(s => s.materialId !== null)
        .toArray();

      // Group by material + stock group
      const balanceMap = new Map<string, { materialId: number; stockGroupId: number; balance: number }>();
      
      // Process stocks
      stocks.forEach(stock => {
        const key = `${stock.materialId}_${stock.stockGroupId}`;
        if (!balanceMap.has(key)) {
          balanceMap.set(key, {
            materialId: stock.materialId,
            stockGroupId: stock.stockGroupId,
            balance: 0
          });
        }
        balanceMap.get(key)!.balance += stock.quantity || 0;
      });
      
      // Process sells (negative)
      sells.forEach(sell => {
        const key = `${sell.materialId}_${sell.stockGroupId}`;
        if (!balanceMap.has(key)) {
          balanceMap.set(key, {
            materialId: sell.materialId,
            stockGroupId: sell.stockGroupId,
            balance: 0
          });
        }
        balanceMap.get(key)!.balance -= Math.abs(sell.quantity || 0);
      });

      // Create snapshots for each material + stock group combination
      const snapshots: IMaterialSnapshot[] = [];
      balanceMap.forEach((data, key) => {
        snapshots.push({
          materialId: data.materialId,
          stockGroupId: data.stockGroupId,
          yearMonth: yearMonth,
          openingBalance: data.balance, // For this implementation, opening = closing
          closingBalance: data.balance
        });
      });

      // Store snapshots in Dexie (they will persist!)
      if (snapshots.length > 0) {
        await db.materialSnapshots.bulkAdd(snapshots);
        console.log(`   💾 Stored ${snapshots.length} snapshots in Dexie for ${yearMonth}`);
      }
      
    } catch (error) {
      console.error('Error creating snapshots for month:', error);
      // Don't throw - let it fall back to old method
    }
  }
}
